import { useState } from 'react';

import * as LucideIcons from 'lucide-react';
import { Database, type LucideIcon } from 'lucide-react';

import { useTheme } from '@/contexts/theme-context';
import { cn } from '@/lib/utils';

interface ConnectorIconProps {
  connector: {
    icon: string;
    iconUrl?: string;
    name: string;
  };
  className?: string;
  /**
   * What the mark is sitting on, which is what decides the variant — not the page theme.
   *
   * `theme` (default) is for a surface that follows the theme (`bg-background`, `bg-card`).
   * `light` is for a surface that is white in both themes: the brand-painted connection cards
   * put the mark on a white tile so it stays legible on all 41 brand colours, and a
   * theme-driven swap turned the seven light-variant marks (OpenAI, Anthropic, Notion,
   * GitHub, AWS, Power BI, Zendesk) white-on-white there.
   */
  surface?: 'theme' | 'light';
}

// Type for accessing Lucide icons by name
type LucideIconsType = Record<string, LucideIcon>;

/**
 * ConnectorIcon - Renders connector icon with fallback support
 *
 * Priority:
 * 1. iconUrl (custom logo from CDN/storage)
 * 2. icon (Lucide icon name)
 * 3. Database (fallback icon)
 */
export function ConnectorIcon({ connector, className, surface = 'theme' }: ConnectorIconProps) {
  const [imageError, setImageError] = useState(false);
  const { resolvedTheme } = useTheme();
  const { icon, iconUrl, name } = connector;

  /*
   * Monochrome marks ship as a pair: `-dark.svg` is the dark glyph (for light surfaces) and
   * `-light.svg` the white one (for dark surfaces). Read from the context rather than
   * `documentElement.classList`, so toggling the theme actually re-renders the icon.
   */
  if (iconUrl && !imageError) {
    let resolvedUrl = iconUrl;
    if (iconUrl.includes('-dark.svg') && surface === 'theme' && resolvedTheme === 'dark') {
      resolvedUrl = iconUrl.replace('-dark.svg', '-light.svg');
    }
    return (
      <img
        src={resolvedUrl}
        alt={`${name} icon`}
        className={cn('object-contain', className)}
        onError={() => setImageError(true)}
      />
    );
  }

  // Try to get Lucide icon by name
  const IconComponent = (LucideIcons as unknown as LucideIconsType)[icon];

  if (IconComponent) {
    return <IconComponent className={className} />;
  }

  // Fallback to Database icon
  return <Database className={className} />;
}

export default ConnectorIcon;
